"use client";

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Toast } from "./Toast";
import type { IToastContextValue, ToastType } from "./types";

interface IToastState {
  id: number;
  message: string;
  type: ToastType;
  autoDismissMs?: number;
}

const ToastContext = createContext<IToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<IToastState | null>(null);
  const counter = useRef(0);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const showToast = (message: string, type: ToastType, autoDismissMs?: number) => {
    counter.current += 1;
    setToast({ id: counter.current, message, type, autoDismissMs });
  };

  const dismissToast = () => setToast(null);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {isMounted &&
        toast &&
        createPortal(
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={dismissToast}
            autoDismissMs={toast.autoDismissMs}
          />,
          document.body
        )}
    </ToastContext.Provider>
  );
}

export function useToast(): IToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
